import { Component, Input, HostBinding, OnChanges, EventEmitter, Output, HostListener, ElementRef, ViewChild, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { MapControls, MapMarker, MapCoordinates, MapRoute } from '../map.component';
import { IKuiDropdownMenuItem } from 'app/key-ui';
import { TranslateService } from '@ngx-translate/core';
import { KuiSnackbarService } from 'app/key-ui/snackbar/snackbar.service';
import { SearchResult } from '../../search-results-list/search-results-list.component';
import { debounce } from 'lodash';
import { ErrorLoggerService, NotificationService, MatchMediaService } from 'app/services';
import { MeasurementUnitsService } from 'app/services/measurement-units/measurement-units.service';
import { MapToolsService } from '../map-tools.service';
import { BaseComponent } from '../../base/base.component';
import { MapToolbarZone } from './components/zones/map-toolbar-zones.component';
import { RouteInput, MapToolbarRoutingComponent } from './components/routing/map-toolbar-routing.component';
import { Observable } from 'rxjs';

export interface MapToolbarLayer {
    title: string;
    id: string;
    visible: boolean;
    action: (layer: MapToolbarLayer) => void;
    meta?: { [key: string]: any }; // allow maps to store library specific information if needed
}

export type ZoneEditorAction = 'zoneaddpolyline' | 'zoneaddpolygon' | 'zoneedit' | 'zonedelete' | 'zoneselect';
export type VisiblePanel = 'routing' | 'layers' | 'measurements' | 'search' | 'coordinates' | ZoneEditorAction;

@Component({
    selector: 'key-map-toolbar',
    templateUrl: './map-toolbar.component.html',
    styleUrls: ['./map-toolbar.component.scss'],
})
export class MapToolbarComponent extends BaseComponent implements OnChanges {

    constructor(
        private i18n: TranslateService,
        private el: ElementRef,
        private snackbar: KuiSnackbarService,
        private ref: ChangeDetectorRef,
        private error: ErrorLoggerService,
        private notify: NotificationService,
        private units: MeasurementUnitsService,
        public media: MatchMediaService,
        private mapTools: MapToolsService
    ) {
        super();
        this.on(this.mapTools.loading$, loading => {
            this.loadingCoordinates = loading === 'coordinates';
            this.searching = loading === 'search';
            this.ref.markForCheck();
        });
    }

    controlsIndex: {
        zoom?: boolean;
        recenter?: boolean;
        fullscreen?: boolean;
        zoneedit?: boolean;
        zonedelete?: boolean;
        zoneadd?: boolean;
        layers?: boolean;
        measurements?: boolean;
        googlemaps?: boolean;
        search?: boolean;
        coordinates?: boolean;
        routing?: boolean;
    } = {};

    zonesDropdown: IKuiDropdownMenuItem[];
    zonesPanels: MapToolbarZone[];

    searchValue: string;
    searchResults: SearchResult<MapCoordinates>[];
    lastSelectedSearchItem: MapMarker;
    searching: boolean;

    coordinatesDetails: { title: string, text: string }[];
    loadingCoordinates = false;
    coordinatesError: string;
    canToggle = true;

    routeInputs: RouteInput[];

    bypassCardOverflow: boolean;

    @HostBinding('class.expanded') expanded = false;

    show: { [key in VisiblePanel]?: boolean } = {
        layers: false,
        zoneaddpolyline: false,
        zoneaddpolygon: false,
        zoneedit: false,
        zonedelete: false,
        zoneselect: false,
        measurements: false,
        search: false,
        coordinates: false,
        routing: false,
    };

    @Input() mapHeight: number;
    @Input() controls: MapControls[];
    @Input() baseLayers: MapToolbarLayer[];
    @Input() overlays: MapToolbarLayer[];
    @Input() isFullscreen: boolean;
    @Input() zonesEditable: boolean;
    @Input() measurementDistance: string;
    @Input() coordinates: MapCoordinates;
    @Input() mapLayerClicked: Observable<{ layer: string, id: string }>;
    @Input() mapLayerDoubleClicked: Observable<{ layer: string, id: string }>;

    @Output() zoomIn = new EventEmitter<void>();
    @Output() zoomOut = new EventEmitter<void>();
    @Output() reset = new EventEmitter<void>();
    @Output() openGoogleMaps = new EventEmitter<MapCoordinates>();
    @Output() zoneActivivated = new EventEmitter<ZoneEditorAction>();
    @Output() zoneCancel = new EventEmitter<ZoneEditorAction>();
    @Output() zoneSave = new EventEmitter<ZoneEditorAction>();
    @Output() zoneUndo = new EventEmitter<ZoneEditorAction>();
    @Output() coordinatesOpen = new EventEmitter<void>();
    @Output() coordinatesClose = new EventEmitter<void>();
    @Output() measurementsOpen = new EventEmitter<void>();
    @Output() measurementsClose = new EventEmitter<void>();
    @Output() measurementsUndo = new EventEmitter<void>();
    @Output() fullscreen = new EventEmitter<void>();
    @Output() markerAdd = new EventEmitter<MapMarker>();
    @Output() markerRemove = new EventEmitter<MapMarker>();
    @Output() routesSet = new EventEmitter<MapRoute[]>();
    @Output() routesClear = new EventEmitter<MapRoute[]>();

    @ViewChild('searchInput') searchInput: ElementRef;
    @ViewChild(MapToolbarRoutingComponent) routingComp: MapToolbarRoutingComponent;

    debouncedUpdateSearchValue = debounce(this.updateSearchValue, 500, { trailing: true, leading: false });

    // close all panels when user clicks outside the toolbar, unless specified to be kept open
    @HostListener('document:click', ['$event']) closeOnOutsideClick(e) {
        const keepOpen = ['zoneaddpolyline', 'zoneaddpolygon', 'zoneedit', 'zonedelete', 'measurements', 'coordinates', 'routing'];
        const current = Object.keys(this.show).find(x => this.show[x]);
        if (!this.el.nativeElement.contains(e.target) && !keepOpen.includes(current)) {
            this.toggleVisiblePanel(null);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.coordinates) {
            this.updateCoordinatesDetails();
        }

        if (this.controls) {
            this.addControlsToIndex(this.controls);
            this.canToggle = this.showToggle(this.controls);

            if (this.controlsIndex.zoneadd || this.controlsIndex.zoneedit || this.controlsIndex.zonedelete) {
                this.zonesDropdown = [
                    ...(this.controlsIndex.zoneadd ? [
                        { text: this.i18n.instant('LEAFLET.DRAW.DRAW_POLYLINE'), type: 'action', closeDropdownOnClicked: true, icon: 'route', action: () => this.activateZone('zoneaddpolyline') },
                        { text: this.i18n.instant('LEAFLET.DRAW.DRAW_POLYGON'), type: 'action', closeDropdownOnClicked: true, icon: 'draw-polygon', action: () => this.activateZone('zoneaddpolygon') },
                    ] : []),
                    this.controlsIndex.zoneedit && { text: this.i18n.instant('LEAFLET.DRAW.EDIT_LAYERS'), type: 'action', closeDropdownOnClicked: true, icon: 'edit', disabled: !this.zonesEditable, action: () => this.activateZone('zoneedit') },
                    this.controlsIndex.zonedelete && { text: this.i18n.instant('LEAFLET.DRAW.DELETE_LAYERS'), type: 'action', closeDropdownOnClicked: true, icon: 'trash', disabled: !this.zonesEditable, action: () => this.activateZone('zonedelete') },
                ].filter(x => x);

                this.zonesPanels = [
                    {
                        key: 'zoneaddpolyline', title: this.i18n.instant('LEAFLET.DRAW.DRAW_POLYLINE'), description: this.i18n.instant('LEAFLET.DRAW.CLICK_TO_START'), buttons: [
                            { text: this.i18n.instant('LEAFLET.DRAW.FINISH'), action: () => this.saveZone('zoneaddpolyline') },
                            { text: this.i18n.instant('LEAFLET.DRAW.DELETE_LAST_POINT'), action: () => this.zoneUndo.emit('zoneaddpolyline') },
                            { text: this.i18n.instant('LEAFLET.DRAW.CANCEL'), action: () => this.cancelZone('zoneaddpolyline') },
                        ],
                    },
                    {
                        key: 'zoneaddpolygon', title: this.i18n.instant('LEAFLET.DRAW.DRAW_POLYGON'), description: this.i18n.instant('LEAFLET.DRAW.CLICK_TO_START'), buttons: [
                            { text: this.i18n.instant('LEAFLET.DRAW.FINISH'), action: () => this.saveZone('zoneaddpolygon') },
                            { text: this.i18n.instant('LEAFLET.DRAW.DELETE_LAST_POINT'), action: () => this.zoneUndo.emit('zoneaddpolygon') },
                            { text: this.i18n.instant('LEAFLET.DRAW.CANCEL'), action: () => this.cancelZone('zoneaddpolygon') },
                        ],
                    },
                    {
                        key: 'zoneedit', title: this.i18n.instant('LEAFLET.DRAW.EDIT_LAYERS'), description: this.i18n.instant('LEAFLET.DRAW.DRAG_HANDLES'), buttons: [
                            { text: this.i18n.instant('LEAFLET.DRAW.SAVE'), action: () => this.saveZone('zoneedit') },
                            { text: this.i18n.instant('LEAFLET.DRAW.CANCEL'), action: () => this.cancelZone('zoneedit') },
                        ],
                    },
                    {
                        key: 'zonedelete', title: this.i18n.instant('LEAFLET.DRAW.DELETE_LAYERS'), description: this.i18n.instant('LEAFLET.DRAW.CLICK_TO_REMOVE'), buttons: [
                            { text: this.i18n.instant('LEAFLET.DRAW.SAVE'), action: () => this.saveZone('zonedelete') },
                            { text: this.i18n.instant('LEAFLET.DRAW.CANCEL'), action: () => this.cancelZone('zonedelete') },
                        ],
                    },
                ];
            }
        }
    }

    updateCoordinatesDetails() {
        this.coordinatesError = null;

        if (!this.coordinates) {
            this.coordinatesDetails = null;
            return;
        }

        if (this.coordinates.error) {
            this.error.trackException(this.coordinates.error);
            this.coordinatesError = this.notify.translateError(this.coordinates.error);
            return;
        }

        const { lat, lon, address, speed } = this.coordinates;
        this.coordinatesDetails = lat && lon && address ? [
            { title: this.i18n.instant('LEAFLET.COORDINATES.TEXT'), text: address },
            { title: this.i18n.instant('LEAFLET.COORDINATES.LAT'), text: (Math.round(lat * 1e6) / 1e6).toString() },
            { title: this.i18n.instant('LEAFLET.COORDINATES.LON'), text: (Math.round(lon * 1e6) / 1e6).toString() },
            speed ? { title: this.i18n.instant('LEAFLET.COORDINATES.SPEED'), text: this.units.format(speed, 'speed').format } : null,
        ].filter(x => x) : [];

        this.ref.markForCheck();
    }

    showToggle(controls: MapControls[]): boolean {
        const checkList = [
            MapControls.zoneadd,
            MapControls.zonedelete,
            MapControls.zoneedit,
            MapControls.layers,
            MapControls.measurements,
            MapControls.googlemaps,
            MapControls.search,
            MapControls.coordinates,
        ];
        return controls.some(x => checkList.includes(x));
    }

    addControlsToIndex(controls: MapControls[]) {
        this.controlsIndex = controls.reduce((result, control) => {
            result[control] = controls.includes(control);
            return result;
        }, {});
    }

    saveZone(type: ZoneEditorAction) {
        this.zoneSave.emit(type);
        this.toggleVisiblePanel(null);
    }

    cancelZone(type: ZoneEditorAction) {
        this.zoneCancel.emit(type);
        this.toggleVisiblePanel(null);
    }

    activateZone(type: ZoneEditorAction) {
        this.zoneActivivated.emit(type);
        this.toggleVisiblePanel(type);
    }

    openCoordinates(state?: boolean) {
        this.coordinatesOpen.emit();
        this.toggleVisiblePanel('coordinates', state);
    }

    closeCoordinates() {
        this.coordinatesClose.emit();
        this.toggleVisiblePanel(null);
    }

    openMeasurements() {
        this.measurementsOpen.emit();
        this.toggleVisiblePanel('measurements');
    }

    closeMeasurements() {
        this.measurementsClose.emit();
        this.toggleVisiblePanel(null);
    }

    openRouting(start: RouteInput) {
        this.bypassCardOverflow = true;
        // if there is a start point send it down to the routing component
        this.routeInputs = start && [start];
        // if we open routing from search we need to clear the search to prevent 2 markers
        this.clearSearch();
        this.toggleVisiblePanel('routing');
    }

    updateRoutes(routes: MapRoute[]) {
        this.bypassCardOverflow = false;
        this.routesSet.emit(routes);
    }

    clearRoutes(routes: MapRoute[]) {
        this.bypassCardOverflow = false;
        this.routesClear.emit(routes);
    }

    toggle(state: boolean) {
        this.toggleVisiblePanel(null);
        this.expanded = state;
    }

    toggleVisiblePanel(panel: VisiblePanel, state?: boolean) {
        const zoneTools = ['zoneaddpolyline', 'zoneaddpolygon', 'zoneedit', 'zonedelete'];
        Object.keys(this.show).forEach(key => {
            const value = this.show[key];
            // if we have zone tools currently open we should inform the map component that it will cancel so that the map can disable editing too
            if (value && zoneTools.includes(key)) {
                this.zoneCancel.emit(key as ZoneEditorAction);
            }
            // if we have measurents open we should inform the map component that it will cancel so that the map can disable editing too
            if (value && key === 'measurements') {
                this.measurementsClose.emit();
            }
            // if we have coordinates open we should inform the map component that it will cancel so that the map can disable editing too
            if (value && !state && key === 'coordinates') {
                this.coordinatesClose.emit();
            }
            // if we have routing open we should inform the map component that it will cancel so that it can remove routes and markers
            if (value && !state && key === 'routing') {
                this.routingComp.markers.forEach(({ marker }) => this.markerRemove.emit(marker));
                this.routesClear.emit(this.routingComp.routes);
            }
            this.show[key] = key === panel && state ? true : (value ? !value : key === panel); // if key is already true, make it false unless state was passed in
        });
    }

    clickLayer(event: MouseEvent, layer: MapToolbarLayer) {
        layer.visible = (event.target as HTMLInputElement).checked;
        layer.action(layer);
    }

    clearMultipleMarkers(markers: MapMarker[]) {
        markers.forEach(x => this.markerRemove.emit(x));
    }

    blurSearchInput() {
        if (this.searchInput) {
            this.searchInput.nativeElement.blur();
        }
    }

    updateSearchValue(value: string) {
        if (!value) {
            this.clearSearch();
            return;
        }

        this.searchValue = value;
        this.toggleVisiblePanel('search', true);
        this.doSearch(value);
        this.ref.markForCheck();
    }

    clearSearch() {
        this.searchValue = '';
        this.toggleVisiblePanel(null);
        this.doSearch(null);
        this.ref.markForCheck();
    }

    hideSearchList() {
        this.toggleVisiblePanel(null);
        this.blurSearchInput();
    }

    async doSearch(term: string) {
        const action = marker => {
            this.markerRemove.emit(this.lastSelectedSearchItem);
            this.lastSelectedSearchItem = marker;
            this.markerAdd.emit(marker);
        };

        this.markerRemove.emit(this.lastSelectedSearchItem);
        this.lastSelectedSearchItem = null;
        this.searchResults = null;

        this.searchResults = await this.mapTools.doSearch(term, action);
        this.ref.markForCheck();
    }

    copyToClipboard(str: string) {
        // create non visible textarea to copy text from
        const el = document.createElement('textarea');
        el.value = str;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);

        // copy text
        const selected =
            document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
        el.select();
        document.execCommand('copy');

        // clean up after yourself
        document.body.removeChild(el);
        if (selected) {
            document.getSelection().removeAllRanges();
            document.getSelection().addRange(selected);
        }

        // inform user of action
        this.snackbar.message(this.i18n.instant('DIALOG.COPIED'));
    }
}
